import { Maybe } from '@codegrade/maybe';
import { array, assert, map, sort, string } from '@codegrade/utils';
/**
 * Get the quiz wrapping a question.
 *
 * @param question The question to get the quiz for.
 * @returns The quiz wrapping the question, or nothing if the question is not
 * wrapped in a quiz.
 */
export function getParentQuiz(question) {
    for (const parent of question.ancestors()) {
        if (parent.isStepOfType(step => step.type === 'quiz')) {
            return Maybe.of(parent);
        }
    }
    return Maybe.empty();
}
/**
 * Orders the answers of a question, respecting the configured answer order.
 *
 * When `answerOrder` is set to `random-order`, the answers are shuffled
 * deterministically using the question ID and user ID as a seed, matching
 * the order the student sees in the UI.
 *
 * @param question The MCQ or Select All question.
 * @param userId The user ID, used to seed the shuffle for randomized answers.
 * @returns The ordered list of answers.
 */
export function orderAnswers(question, userId) {
    const entries = Object.entries(question.answers);
    const sorted = sort.sortBy(entries, ([, a]) => [a.index]);
    if (question.answerOrder.orDefault('index-order') === 'random-order') {
        array.seededShuffle(sorted, `${question.id}-${userId}`);
    }
    return sorted;
}
/**
 * Split the answer file into lines for each answer, including the line offset.
 *
 * @param config The quiz configuration. If `Nothing` is passed only questions
 * for which an answer is present in the answers file are considered.
 * @param answers The answers file uploaded by a student.
 * @returns The lines and offset of each answer in the answers file.
 */
function splitAnswers(config, { answers }) {
    const questionsAnswers = Object.entries(answers).map(([questionId, answer]) => ({
        tag: answer.tag,
        questionId,
        answer,
    }));
    const seenIds = new Set(questionsAnswers.map(a => a.questionId));
    config.ifJust(({ questions }) => {
        for (const question of questions) {
            if (seenIds.has(question.id)) {
                continue;
            }
            questionsAnswers.push({
                tag: question.step.type,
                questionId: question.id,
                answer: undefined,
            });
        }
    });
    const sorted = sort.sortBy(questionsAnswers, ({ questionId, answer }) => [
        // Sort questions with an answer present before questions without an
        // answer to keep backwards compatibility with old plagiarism runs where
        // we only considered questions with an answer.
        answer == null ? 1 : -1,
        questionId,
    ]);
    let offset = 0;
    const ret = [];
    for (const { tag, questionId, answer } of sorted) {
        switch (tag) {
            case 'mcq':
                ret.push({ tag, questionId, offset });
                offset += 1;
                break;
            case 'select-all-question':
                ret.push({ tag, questionId, offset });
                offset += 1;
                break;
            case 'prompt-engineering-question': {
                const split = {
                    tag,
                    questionId,
                    offset,
                    lines: [],
                };
                if (answer != null) {
                    assert.AssertionError.assert(answer.tag === tag);
                    const lines = string.splitLines(answer.answer);
                    if (lines.length > 0 && lines[lines.length - 1] === '') {
                        lines.pop();
                    }
                    split.lines = lines;
                }
                ret.push(split);
                offset = Math.max(1, split.lines.length);
                break;
            }
            case 'coding-question': {
                const split = {
                    tag,
                    questionId,
                    offset,
                    lines: [],
                    language: Maybe.empty(),
                };
                if (answer != null) {
                    assert.AssertionError.assert(answer.tag === tag);
                    split.language = answer.language;
                    const lines = string.splitLines(answer.answer);
                    if (lines.length > 0 && lines[lines.length - 1] === '') {
                        lines.pop();
                    }
                    split.lines = lines;
                }
                ret.push(split);
                offset += Math.max(1, split.lines.length);
                break;
            }
            /* c8 ignore next */
            default:
                /* c8 ignore next */
                assert.AssertionError.assertNever(tag);
        }
    }
    return ret;
}
/**
 * Get the line offset of each answer in the given answers file.
 *
 * @param config The quiz configuration.
 * @param answers The answers file uploaded by a student.
 * @returns The line offset of each answer in the answers file.
 */
export function getAnswerLineOffsets(config, answers) {
    const split = splitAnswers(Maybe.of(config), answers);
    return new map.MaybeMap(split.map(ans => [ans.questionId, ans.offset]));
}
/**
 * Split all Coding question answers of an answers file into lines, and calculate the line offset for each answer.
 *
 * Each Multiple Choice and Select All question is counted as 1 single line.
 *
 * @param answers The answers file uploaded by a student.
 * @returns An ordered list of Coding question answers. The list is ordered by question id.
 */
export function splitCodingQuestionLines(answers) {
    return splitAnswers(Maybe.empty(), answers).filter((ans) => ans.tag === 'coding-question');
}
/**
 * Get the language to use for a coding question.
 *
 * The language identifiers returned here correspond to HighlightJS language identifiers.
 *
 * Note: the extensions checked here should match the file extensions set on the supported
 * implementations of the JPlag `Language`.
 * See plagiarism/jplag-runner/src/main/java/com/codegrade/jplag_runner/languages/QuizLanguage.java
 * for a list of supported languages.
 *
 * @param ext The extension of the file.
 * @returns The language to use for this step.
 */
export function getProgrammingLanguage(ext) {
    switch (ext?.toLocaleLowerCase()) {
        case 'c':
        case 'cc':
        case 'cpp':
        case 'cxx':
        case 'h':
        case 'hh':
        case 'hpp':
        case 'hxx':
            return 'cpp';
        case 'cs':
            return 'csharp';
        case 'java':
            return 'java';
        case 'es':
        case 'esm':
        case 'js':
        case 'jsm':
            return 'javascript';
        case 'php':
            return 'php';
        case 'r':
            return 'r';
        case 'sc':
        case 'scala':
            return 'scala';
        case 'py':
        default:
            return 'python';
    }
}
/**
 * Wrapper around getProgrammingLanguage for getting the programming language used in
 * the coding quiz question.
 *
 * @param studentFileName File name to retrieve the extension of.
 * @returns A string denoting the language based on the file extension.
 */
export function getCodingQuestionLanguage(studentFileName) {
    const ext = studentFileName.split('.').pop();
    return getProgrammingLanguage(ext);
}
/**
 * Set the language to use for each coding question answer in an answers file.
 *
 * @param answersFile The answers file to update.
 * @param config The AutoTest configuration.
 * @returns The updated answers file.
 */
export function updateCodingQuestionAnswers(answersFile, config) {
    const answers = { ...answersFile.answers };
    for (const step of config.steps) {
        if (step.type !== 'coding-question') {
            continue;
        }
        const answer = answers[step.id];
        if (answer == null || answer.tag !== 'coding-question') {
            continue;
        }
        answers[step.id] = {
            ...answer,
            language: Maybe.of(getCodingQuestionLanguage(step.studentFileName)),
        };
    }
    return { answers };
}
